export namespace SimpleModel {
  export type Data<T> = T;
  export type Mutator<T> = (data: T) => void;
  export type Updater<T> = (data: T) => T;
}

export class SimpleModel<T extends { [key: string]: any }> {
  protected static readonly JSON_MEMOIZED = '@@__JSON_MEMOIZED__@@';

  private memoized: { [key: string]: unknown } = {};
  private data: T;

  constructor(initialData: SimpleModel.Data<T>) {
    this.data = { ...initialData };
  }

  clone(): this {
    const Cls = Object.getPrototypeOf(this).constructor;
    return new Cls({ ...this.data });
  }

  update<K extends keyof T>(key: K, updater: (v: T[K]) => T[K]): this {
    this.data[key] = updater(this.data[key]);
    return this.clone();
  }

  get<K extends keyof T>(key: K, notSetValue?: T[K]): T[K] {
    return this.data.hasOwnProperty(key) ? this.data[key] : notSetValue!;
  }

  set<K extends keyof T>(key: K, value: T[K]): this {
    this.data[key] = value;
    return this.clone();
  }

  has<K extends keyof T>(key: K): boolean {
    return this.data.hasOwnProperty(key);
  }

  remove<K extends keyof T>(key: K): this {
    if (this.data.hasOwnProperty(key)) {
      const { [key]: _, ...rest } = this.data;
      this.data = rest as T;
    }
    return this.clone();
  }

  withMutations(mutator: (draft: T) => void): this {
    const draft = { ...this.data } as T;
    mutator(draft);
    this.data = draft;
    return this.clone();
  }

  equals(model: unknown): boolean {
    if (!(model instanceof SimpleModel)) return false;
    const a = this.data;
    const b = model.data;
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;

    return keysA.every((key) => Object.is(a[key], b[key]));
  }

  toJSON(): unknown {
    return this.memoize(SimpleModel.JSON_MEMOIZED, (data) => ({ ...data }));
  }

  protected memoize<M>(key: string, getter: (data: T) => M): M {
    if (!this.memoized.hasOwnProperty(key)) {
      this.memoized[key] = getter(this.data);
    }
    return this.memoized[key] as M;
  }
}
